import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ListingImage } from "@/components/ListingImage";
import DistanceBadge from "@/components/DistanceBadge";
import { ORDER_STATUSES, lookup } from "@/lib/grocery";
import {
  Truck, Package, Upload, CheckCircle2, MapPin, Phone, Camera, Loader2, Map as MapIcon
} from "lucide-react";
import { toast } from "sonner";

const StatusBadge = ({ status, lang }) => {
  const s = ORDER_STATUSES.find((x) => x.v === status);
  if (!s) return null;
  return <Badge className={s.color}>{lang === "ar" ? s.ar : s.en}</Badge>;
};

const NEXT_STATUS = {
  shopping_in_progress: "receipt_uploaded",
  receipt_uploaded: "out_for_delivery",
  out_for_delivery: "delivered"
};

export default function DriverGroceryDashboard() {
  const { t, language } = useLanguage();
  const { token } = useAuth();
  const [queue, setQueue] = useState([]);
  const [mine, setMine] = useState([]);
  const [tab, setTab] = useState("mine");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({});
  const fileRef = useRef({});

  const load = async () => {
    try {
      const [q, m] = await Promise.all([
        axios.get(`${API}/orders/driver/queue`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/orders/driver/mine`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setQueue(q.data); setMine(m.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { load(); const i = setInterval(load, 8000); return () => clearInterval(i); }, []); // eslint-disable-line

  const claim = async (id) => {
    try {
      await axios.post(`${API}/orders/${id}/claim`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("تم الاستلام — ابدأ التسوّق", "Claimed — start shopping"));
      setTab("mine"); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("فشل", "Failed")); }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.post(`${API}/orders/${id}/status?new_status=${status}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("تم التحديث", "Updated"));
      load();
    } catch (e) { toast.error(t("فشل", "Failed")); }
  };

  const uploadReceipt = async (orderId, file) => {
    setUploading((u) => ({ ...u, [orderId]: true }));
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = await axios.post(`${API}/uploads/image`, fd, { headers: { Authorization: `Bearer ${token}` } });
      await axios.post(`${API}/orders/${orderId}/receipt?image_path=${encodeURIComponent(up.data.path)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t("تم رفع الفاتورة", "Receipt uploaded"));
      load();
    } catch (e) { toast.error(t("فشل الرفع", "Upload failed")); }
    finally { setUploading((u) => ({ ...u, [orderId]: false })); }
  };

  const list = tab === "queue" ? queue : mine;

  return (
    <DashboardLayout title={t("لوحة السائق - البقالة", "Driver Dashboard — Grocery")}>
      <div className="space-y-5" data-testid="driver-grocery">
        <div className="glass-card rounded-3xl p-6 bg-luxury relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-80 h-80 bg-yellow-500/20 blur-[100px] rounded-full" />
          <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-purple-500/20 blur-[100px] rounded-full" />
          <div className="relative">
            <Badge className="bg-gradient-to-r from-purple-500/20 to-yellow-500/20 text-yellow-300 border-yellow-500/30 mb-2">
              <Truck size={12} className="ml-1" /> {t("التوصيل", "Delivery")}
            </Badge>
            <h2 className="text-2xl font-bold text-luxury">{t("اطلب من السوق، صوّر الفاتورة، وصّل بسرعة", "Shop, snap the receipt, deliver fast")}</h2>
          </div>
        </div>

        <div className="flex gap-2">
          <button onClick={() => setTab("mine")} className={`px-5 py-2 rounded-full ${tab === "mine" ? "bg-yellow-500 text-black" : "bg-slate-900 text-slate-300"}`} data-testid="drv-tab-mine">
            {t("طلباتي", "My orders")} ({mine.length})
          </button>
          <button onClick={() => setTab("queue")} className={`px-5 py-2 rounded-full ${tab === "queue" ? "bg-yellow-500 text-black" : "bg-slate-900 text-slate-300"}`} data-testid="drv-tab-queue">
            {t("الطلبات المتاحة", "Open queue")} ({queue.length})
          </button>
        </div>

        {loading ? (
          <div className="glass-card rounded-2xl h-40 skeleton" />
        ) : list.length === 0 ? (
          <div className="glass-card rounded-2xl p-12 text-center text-slate-400">
            {tab === "queue" ? t("لا توجد طلبات متاحة الآن", "No open orders right now") : t("لا طلبات لديك بعد", "No orders yet — claim one from the queue")}
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-4">
            {list.map((o) => {
              const mapsUrl = o.store_id
                ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(o.store_name || "")}`
                : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(o.delivery_address + ", " + o.delivery_city + ", " + o.delivery_state + " " + o.delivery_zip)}`;
              const nextStatus = NEXT_STATUS[o.status];
              return (
                <div key={o.order_id} className="glass-card rounded-2xl p-5 purple-gold-glow" data-testid={`drv-order-${o.order_id}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <span className="text-xs text-slate-500">#{o.order_id.slice(-6)}</span>
                      <h3 className="font-bold text-white">{o.store_name || t("قائمة تسوق مخصصة", "Custom list")}</h3>
                      <p className="text-xs text-slate-400">{o.customer_name} · {o.order_type === "shopping_list" ? t("قائمة", "List") : t("منتجات", "Cart")}</p>
                    </div>
                    <StatusBadge status={o.status} lang={language} />
                  </div>

                  <div className="bg-slate-900 rounded-xl p-2 mb-3 max-h-32 overflow-y-auto space-y-1">
                    {o.items.map((it, i) => (
                      <p key={i} className="text-xs text-slate-300">• {it.quantity} {it.unit} · {it.name}{it.notes ? ` (${it.notes})` : ""}</p>
                    ))}
                  </div>

                  <div className="space-y-1 text-xs text-slate-400 mb-3">
                    <p className="flex items-center gap-1"><MapPin size={12} className="text-yellow-500" />{o.delivery_address}, {o.delivery_city}, {o.delivery_state} {o.delivery_zip}</p>
                    <p className="flex items-center gap-1"><Phone size={12} className="text-yellow-500" />{o.delivery_phone}</p>
                    {o.delivery_notes && <p className="text-purple-300">{o.delivery_notes}</p>}
                    {tab === "mine" && o.store_name && (
                      <div className="pt-1">
                        <DistanceBadge
                          origin={o.store_name}
                          destination={`${o.delivery_address}, ${o.delivery_city}, ${o.delivery_state} ${o.delivery_zip}`}
                        />
                      </div>
                    )}
                  </div>

                  {o.receipt_images?.length > 0 && (
                    <div className="flex gap-1 mb-3 overflow-x-auto">
                      {o.receipt_images.map((p, i) => (
                        <div key={i} className="w-16 h-16 rounded border border-slate-700 overflow-hidden flex-shrink-0">
                          <ListingImage path={p} alt="" className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-2 flex-wrap">
                    {tab === "queue" ? (
                      <Button onClick={() => claim(o.order_id)} className="btn-primary flex-1" data-testid={`claim-${o.order_id}`}>
                        <Truck size={14} className="ml-2" /> {t("استلم الطلب", "Claim order")}
                      </Button>
                    ) : (
                      <>
                        <a href={mapsUrl} target="_blank" rel="noreferrer" className="flex-1">
                          <Button className="w-full" variant="outline"><MapIcon size={14} className="ml-2" />{t("الخرائط", "Maps")}</Button>
                        </a>
                        <input
                          ref={(el) => (fileRef.current[o.order_id] = el)}
                          type="file" accept="image/*" className="hidden"
                          onChange={(e) => e.target.files?.[0] && uploadReceipt(o.order_id, e.target.files[0])}
                          data-testid={`receipt-input-${o.order_id}`}
                        />
                        <Button onClick={() => fileRef.current[o.order_id]?.click()} disabled={uploading[o.order_id]} variant="outline" className="border-purple-500/40 text-purple-300" data-testid={`upload-receipt-${o.order_id}`}>
                          {uploading[o.order_id] ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
                        </Button>
                        {nextStatus && (
                          <Button onClick={() => updateStatus(o.order_id, nextStatus)} className="btn-primary" data-testid={`advance-${o.order_id}`}>
                            {nextStatus === "delivered" ? <CheckCircle2 size={14} className="ml-2" /> : <Package size={14} className="ml-2" />}
                            {t(lookup(ORDER_STATUSES, nextStatus, "ar"), lookup(ORDER_STATUSES, nextStatus, "en"))}
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
